import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

// import { ReactComponent as BlackCarIcon } from '../../assets/icons/black-car-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/copy.svg';
// import { ReactComponent as EmailIcon } from '../../assets/icons/email-icon.svg';
import { ReactComponent as FlashIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as NoActiveIcon } from '../../assets/icons/no-active-sessions.svg';
import { ReactComponent as StopFillIcon } from '../../assets/icons/stop-fill-icon.svg';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import occpEvent from '../../utils/api/ocpp-events';
import paymentsApi from '../../utils/api/payments';
import MyButton from '../button/Button';
import ModalBox from '../modal-box/ModalBox';
import DeviceSidePanel from '../table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../table-side-panel/LocationSidePanel';
import SidePaneModel from '../table-side-panel/SidePaneModel';
import TariffSidePanel from '../table-side-panel/TariffSidePanel';

interface NetworkActiveSessionProperties {
  dataList?: any;
  sessionId?: string;
  transactionId?: string;
  onStopSuccess?: () => void;
  autoRefreshInterval?: number;
}

/**
 * Convert lat/lng strings to valid Google map position
 * @param {string} [lat] - Latitude string
 * @param {string} [lng] - Longitude string
 * @returns {{lat: number, lng: number} | null} Google maps LatLngLiteral or null
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z" fill="${color}"/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z" fill="white"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Calculates and formats duration for active session
 * @param {any} session - The active session data
 * @returns {string} Formatted duration string
 */
const getSessionDuration = (session: any): string => {
  if (session?.durationSeconds !== undefined && session?.durationSeconds !== null) {
    const totalSecs = Math.max(0, Math.floor(Number(session.durationSeconds)));
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;
    return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }
  const startString = session?.transactionStartsAt || session?.sessionStartAt || session?.createdAt;
  if (startString) {
    const start = new Date(startString).getTime();
    const now = Date.now();
    if (!Number.isNaN(start) && start > 0) {
      const diffSecs = Math.max(0, Math.floor((now - start) / 1000));
      const hours = Math.floor(diffSecs / 3600);
      const minutes = Math.floor((diffSecs % 3600) / 60);
      const seconds = diffSecs % 60;
      return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    }
  }
  return '--';
};

/**
 * Network active charging session component.
 * @param {NetworkActiveSessionProperties} properties - Component properties.
 * @returns {React.JSX.Element} Network active charging session component.
 */
function NetworkActiveChargingSession(
  properties: NetworkActiveSessionProperties,
): React.JSX.Element {
  const {
    dataList,
    sessionId,
    transactionId,
    onStopSuccess,
    autoRefreshInterval = 10000,
  } = properties;
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [isStop, setIsStop] = useState<boolean>(false);
  const [isForceStop, setIsForceStop] = useState<boolean>(false);
  const [fetchedSession, setFetchedSession] = useState<any>(null);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { isLoaded } = useGoogleMaps();

  const hasPartnerPermission =
    permissionCheck('cpoPartnerView') || permissionCheck('cpoPartnerUpdate');

  const onStopSuccessReference = useRef(onStopSuccess);
  onStopSuccessReference.current = onStopSuccess;

  const targetId = useMemo(() => {
    return (
      transactionId || sessionId || dataList?.transactionId || dataList?.ocpiId || dataList?._id
    );
  }, [transactionId, sessionId, dataList?.transactionId, dataList?.ocpiId, dataList?._id]);

  useEffect(() => {
    setFetchedSession(null);
  }, [targetId]);

  const activeSessionInfo = fetchedSession || dataList || {};

  /**
   * Fetch single session details by ID.
   * @returns {Promise<void>} Resolves when session details are fetched.
   */
  const fetchSessionDetails = useCallback(async (): Promise<void> => {
    if (!targetId && !dataList) return;

    try {
      const response = await paymentsApi.fetchChargingSessions({
        transactionId: targetId,
        limit: 1,
      });
      if (response?.data?.results?.[0]) {
        setFetchedSession(response.data.results[0]);
      }
    } catch {
      // Ignore quiet background errors
    }
  }, [targetId, dataList]);

  const fetchSessionDetailsReference = useRef(fetchSessionDetails);
  fetchSessionDetailsReference.current = fetchSessionDetails;

  /**
   * Handle refresh details callback for side panels.
   */
  const handleRefreshDetails = useCallback((): void => {
    onStopSuccessReference.current?.();
    fetchSessionDetailsReference.current();
  }, []);

  const sidePanelSessions = useMemo(() => {
    return activeSessionInfo && Object.keys(activeSessionInfo).length > 0
      ? [activeSessionInfo]
      : [];
  }, [activeSessionInfo]);

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
  } = useTableSidePanel({
    onRefresh: handleRefreshDetails,
    sessions: sidePanelSessions,
  });

  const [modelDetails, setModelDetails] = useState<{
    status: boolean;
    title: string;
    url?: string;
    newTabUrl?: string;
    hideHeader?: boolean;
    content: React.JSX.Element | null;
  }>({
    status: false,
    title: '',
    url: '',
    newTabUrl: '',
    hideHeader: false,
    content: null,
  });

  /**
   * Handle close side panel model.
   */
  const handleClosePanel = (): void => {
    setModelDetails((previous) => ({
      ...previous,
      status: false,
      content: null,
    }));
    handleSidePanelClose();
  };

  /**
   * Handle side panel operation success callback.
   */
  const handleSidePanelSuccess = useCallback((): void => {
    handleEditModeChange(false);
    onStopSuccessReference.current?.();
    fetchSessionDetailsReference.current();
  }, [handleEditModeChange]);

  const queryStationId = searchParameters.get('stationId');
  const queryDeviceId = searchParameters.get('deviceId');
  const queryTariffId = searchParameters.get('tariffId');
  const queryAccountCustomId = searchParameters.get('accountCustomId');

  useEffect(() => {
    if (queryStationId) {
      setModelDetails({
        status: true,
        title: t('sidePanel:stationDetails', { defaultValue: 'Station Details' }),
        url: '',
        newTabUrl: '',
        hideHeader: false,
        content: (
          <StationSidePanel
            fetchIdFrom="queryParams"
            initialIsEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            onSuccess={handleSidePanelSuccess}
          />
        ),
      });
    } else if (queryDeviceId) {
      setModelDetails({
        status: true,
        title: t('sidePanel:deviceDetails', { defaultValue: 'Charger Details' }),
        url: '',
        newTabUrl: '',
        hideHeader: false,
        content: (
          <DeviceSidePanel
            fetchIdFrom="queryParams"
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            onSuccess={handleSidePanelSuccess}
          />
        ),
      });
    } else if (queryTariffId) {
      setModelDetails({
        status: true,
        title: t('sidePanel:tariffDetails', { defaultValue: 'Tariff Details' }),
        url: `/ev-admin/tariffs/view/${queryTariffId}`,
        newTabUrl: `/ev-admin/tariffs/view/${queryTariffId}`,
        hideHeader: false,
        content: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      });
    } else if (queryAccountCustomId) {
      setModelDetails({
        status: true,
        title: t('account:facilityDetails', { defaultValue: 'Partner Details' }),
        url: '',
        newTabUrl: '',
        hideHeader: false,
        content: (
          <FacilityDetailsSidePanel
            actionActive
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            fetchIdFrom="queryParams"
            onSuccess={handleSidePanelSuccess}
          />
        ),
      });
    } else {
      setModelDetails((previous) => {
        if (!previous.status && previous.content === null) {
          return previous;
        }
        return {
          ...previous,
          status: false,
          content: null,
        };
      });
    }
  }, [
    queryStationId,
    queryDeviceId,
    queryTariffId,
    queryAccountCustomId,
    isEditMode,
    handleSidePanelSuccess,
    handleEditModeChange,
    t,
  ]);

  /**
   * Auto-polling for live session telemetry updates.
   */
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return undefined;

    const timer = setInterval(() => {
      if (onStopSuccessReference.current) {
        onStopSuccessReference.current();
      } else {
        fetchSessionDetailsReference.current();
      }
    }, autoRefreshInterval);

    return () => clearInterval(timer);
  }, [autoRefreshInterval]);

  /**
   * Handle Station click to open StationSidePanel
   * @param {React.MouseEvent} event - Mouse click event
   */
  const handleStationClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    const rawStation =
      activeSessionInfo?.chargingStationId ||
      activeSessionInfo?.chargingStationDetails?._id ||
      activeSessionInfo?.chargingStationDetails?.id ||
      activeSessionInfo?.stationId ||
      activeSessionInfo?.chargingStation;

    if (!rawStation) return;

    // Only proceed if rawStation is a valid ID (e.g., 24-char hex or non-space ID string)
    const isValidId =
      /^[a-f\d]{24}$/i.test(rawStation) ||
      (typeof rawStation === 'string' && !rawStation.includes(' '));

    if (!isValidId) return;

    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'tariffId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));
    parameters.set('stationId', rawStation);
    setSearchParameters(parameters);
    dispatch(
      setSidePanelData({
        key: 'CommercialSessions',
        value: activeSessionInfo._id || activeSessionInfo.id,
      }),
    );
  };

  /**
   * Handle Charger/Device click to open DeviceSidePanel
   * @param {React.MouseEvent} event - Mouse click event
   */
  const handleDeviceClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    const deviceId =
      activeSessionInfo?.chargingPointSn ||
      activeSessionInfo?.chargingPointId ||
      activeSessionInfo?.deviceId ||
      (typeof activeSessionInfo?.chargingPointName === 'string' &&
      !activeSessionInfo.chargingPointName.includes(' ')
        ? activeSessionInfo.chargingPointName
        : undefined);

    if (!deviceId) return;

    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'tariffId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));
    parameters.set('deviceId', deviceId);
    setSearchParameters(parameters);
    dispatch(
      setSidePanelData({
        key: 'CommercialSessions',
        value: activeSessionInfo._id || activeSessionInfo.id,
      }),
    );
  };

  /**
   * Handle Tariff click to open TariffSidePanel
   * @param {React.MouseEvent} event - Mouse click event
   */
  const handleTariffClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    const tariffId =
      activeSessionInfo?.tariff?.tariffId ||
      activeSessionInfo?.tariff?.ocpiId ||
      activeSessionInfo?.tariffId;
    if (!tariffId) return;

    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'tariffId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));
    parameters.set('tariffId', tariffId);
    setSearchParameters(parameters);
    dispatch(
      setSidePanelData({
        key: 'CommercialSessions',
        value: activeSessionInfo._id || activeSessionInfo.id,
      }),
    );
  };

  /**
   * Handle Partner / Account click to open FacilityDetailsSidePanel
   * @param {React.MouseEvent} event - Mouse click event
   */
  const handlePartnerClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (!hasPartnerPermission) return;
    const accountCustomId = activeSessionInfo?.accountCustomId || activeSessionInfo?.accountId;
    if (!accountCustomId) return;

    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'tariffId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));
    parameters.set('accountCustomId', accountCustomId);
    setSearchParameters(parameters);
    dispatch(
      setSidePanelData({
        key: 'CommercialSessions',
        value: activeSessionInfo._id || activeSessionInfo.id,
      }),
    );
  };

  const energyVolume = useMemo(() => {
    const periods = activeSessionInfo?.chargingPeriods;
    if (!Array.isArray(periods) || periods.length === 0) return undefined;
    const lastPeriod = periods[periods.length - 1];
    const dimensions = lastPeriod?.dimensions;
    if (!Array.isArray(dimensions)) return undefined;
    return dimensions.find((dimension: any) => dimension?.type === 'ENERGY')?.volume;
  }, [activeSessionInfo?.chargingPeriods]);

  const energyKwh = useMemo(() => {
    return (
      energyVolume ??
      activeSessionInfo?.powerConsumedInKwh ??
      activeSessionInfo?.totalEnergy ??
      activeSessionInfo?.energyKwh ??
      0
    );
  }, [
    energyVolume,
    activeSessionInfo?.powerConsumedInKwh,
    activeSessionInfo?.totalEnergy,
    activeSessionInfo?.energyKwh,
  ]);

  const evseUid =
    activeSessionInfo?.evseUid ||
    activeSessionInfo?.cdrLocation?.evseUid ||
    (activeSessionInfo?.chargingPointSn && activeSessionInfo?.connectorId
      ? `${activeSessionInfo.chargingPointSn}_${activeSessionInfo.connectorId}`
      : activeSessionInfo?.chargingPointSn || '--');

  const mapCenter = useMemo(() => {
    const lat =
      activeSessionInfo?.latitude ||
      activeSessionInfo?.chargingStationDetails?.latitude ||
      activeSessionInfo?.cdrLocation?.latitude;
    const lng =
      activeSessionInfo?.longitude ||
      activeSessionInfo?.chargingStationDetails?.longitude ||
      activeSessionInfo?.cdrLocation?.longitude;
    return getValidPosition(lat, lng);
  }, [activeSessionInfo]);

  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  /**
   * Handle stop button click.
   * @returns {Promise<void>} A promise that resolves when transaction stop request succeeds.
   */
  const handleStop = async (): Promise<void> => {
    const chargingPointSn = activeSessionInfo?.chargingPointSn;
    const currentTransactionId =
      activeSessionInfo?.transactionId ||
      activeSessionInfo?.transactionID ||
      activeSessionInfo?.sessionId ||
      activeSessionInfo?.id;

    if (!chargingPointSn || !currentTransactionId) return;

    const payload = {
      chargingPointSn,
      transactionId: currentTransactionId,
    };

    try {
      setIsStop(true);
      await occpEvent.remoteStopTransaction(payload);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('transactionStopRequestSuccess', {
            defaultValue: 'Transaction stop requested successfully',
          }),
          type: 'success',
          code: 200,
        }),
      );
      onStopSuccess?.();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message ||
            t('errorMessage', { defaultValue: 'Something went wrong' }),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setIsStop(false);
    }
  };

  /**
   * Handle force stop button click.
   * @returns {Promise<void>} A promise that resolves when transaction force stop request succeeds.
   */
  const handleForceStop = async (): Promise<void> => {
    const chargingPointSn = activeSessionInfo?.chargingPointSn;
    const currentTransactionId =
      activeSessionInfo?.transactionId ||
      activeSessionInfo?.transactionID ||
      activeSessionInfo?.sessionId ||
      activeSessionInfo?.id;

    if (!chargingPointSn || !currentTransactionId) return;

    const payload = {
      chargingPointSn,
      transactionId: currentTransactionId,
      forceStop: true,
    };

    try {
      setIsForceStop(true);
      await occpEvent.remoteStopTransaction(payload);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('transactionForceStopRequestSuccess', {
            defaultValue: 'Transaction force stop requested successfully',
          }),
          type: 'success',
          code: 200,
        }),
      );
      onStopSuccess?.();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message ||
            t('errorMessage', { defaultValue: 'Something went wrong' }),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setIsForceStop(false);
    }
  };

  /**
   * Copy text to clipboard and trigger notification.
   * @param {string} text - The text to copy.
   */
  const handleCopyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
    dispatch(
      setNotificationMessage({
        status: true,
        message: t('copiedToClipboard') || 'Copied to clipboard!',
        type: 'success',
        code: 200,
      }),
    );
  };

  if (!dataList) {
    return (
      <div className="active-charging-session-wrapper" dir={i18n.dir()}>
        <div className="no-active-session">
          <NoActiveIcon />
          <p>{t('noActiveSession', { defaultValue: 'No active session found.' })}</p>
        </div>
      </div>
    );
  }

  const isAdminStart = (activeSessionInfo as any)?.isChargingPointCheck === true;

  return (
    <div className="network-active-charging-session-wrapper" dir={i18n.dir()}>
      <div className="active-charging-session-split-container">
        {/* LEFT COLUMN */}
        <div className="split-column-left">
          <div className="header-buttons">
            <div
              title={
                isAdminStart
                  ? 'Session started by the admin, please go to the admin OCPP console to stop it.'
                  : ''
              }>
              <MyButton
                label={t('stop', { defaultValue: 'Stop' })}
                buttonType="danger-outline"
                onClickFunc={isAdminStart ? undefined : handleStop}
                loading={isStop}
                disableButton={isAdminStart || isStop || isForceStop}
                svgIcon={<StopFillIcon />}
              />
            </div>
            <div
              title={
                isAdminStart
                  ? 'Session started by the admin, please go to the admin OCPP console to stop it.'
                  : ''
              }>
              <MyButton
                label={t('forceStop', { defaultValue: 'Force Stop' })}
                buttonType="error"
                onClickFunc={isAdminStart ? undefined : handleForceStop}
                loading={isForceStop}
                disableButton={isAdminStart || isStop || isForceStop}
                svgIcon={<StopFillIcon />}
              />
            </div>
          </div>

          {/* Driver info card */}
          <div className="driver-info-card">
            {/* <div className="driver-image-wrapper">
              <BlackCarIcon className="driver-vehicle-img default" />
            </div> */}
            <div className="driver-details">
              <p className="driver-name">
                {activeSessionInfo?.facilityAccountName ||
                  activeSessionInfo?.accountCustomId ||
                  activeSessionInfo?.facilityTagName ||
                  '--'}
              </p>
              {/* <p className="driver-id">
                {activeSessionInfo?.accountCustomId || activeSessionInfo?.accountId || '--'}
              </p> */}
              <div className="driver-contact-row">
                {/* <span className="driver-email">
                  <EmailIcon className="email-svg" />
                  {activeSessionInfo?.email || '--'}
                </span> */}
                {activeSessionInfo?.accountCustomId || activeSessionInfo?.accountId ? (
                  <span
                    className={hasPartnerPermission ? 'driver-email hyperlink' : 'driver-email'}
                    role="presentation"
                    onClick={hasPartnerPermission ? handlePartnerClick : undefined}>
                    {activeSessionInfo.accountCustomId || activeSessionInfo.accountId}
                  </span>
                ) : (
                  <span className="driver-email">--</span>
                )}
                <span className="live-status-badge">
                  <span className="status-dot green" />
                  {activeSessionInfo?.status === 'inProgress'
                    ? 'In Progress'
                    : activeSessionInfo?.status || 'In Progress'}
                </span>
              </div>
            </div>
          </div>
          {/* Location & Charger info card */}
          <div className="location-charger-info-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <LocationIcon className="location-icon" />
              </div>
              <span className="card-title">
                {t('locationAndCharger', { defaultValue: 'Location & Charger' })}
              </span>
            </div>
            <div className="card-grid">
              <div className="grid-item">
                <span className="grid-label">
                  {t('chargingStation', { defaultValue: 'Charging Station' })}
                </span>
                {activeSessionInfo?.chargingStation ? (
                  <span
                    className="grid-value hyperlink"
                    role="presentation"
                    onClick={handleStationClick}>
                    {activeSessionInfo.chargingStation}
                  </span>
                ) : (
                  <span className="grid-value">--</span>
                )}
              </div>
              <div className="vertical-line-divider" />
              <div className="grid-item">
                <span className="grid-label">
                  {t('chargerName', { defaultValue: 'Charger Name / SN' })}
                </span>
                {activeSessionInfo?.chargingPointName || activeSessionInfo?.chargingPointSn ? (
                  <span
                    className="grid-value hyperlink"
                    role="presentation"
                    onClick={handleDeviceClick}>
                    {activeSessionInfo.chargingPointName || activeSessionInfo.chargingPointSn}
                  </span>
                ) : (
                  <span className="grid-value">--</span>
                )}
              </div>
              <div className="vertical-line-divider" />
              <div className="grid-item">
                <span className="grid-label">{t('evseUid', { defaultValue: 'EVSE UID' })}</span>
                <span className="grid-value">{evseUid}</span>
              </div>
              <div className="vertical-line-divider" />
              <div className="grid-item">
                <span className="grid-label">
                  {t('connectorId', { defaultValue: 'Connector ID' })}
                </span>
                <span className="grid-value">
                  {activeSessionInfo?.connectorId ??
                    activeSessionInfo?.cdrLocation?.connectorId ??
                    '--'}
                </span>
              </div>
            </div>
          </div>

          {/* Session Timestamps bar */}
          <div className="session-timestamps-bar">
            <div className="timestamp-item">
              <CalendarIcon className="timestamp-icon" />
              <div className="timestamp-details">
                <span className="timestamp-label">
                  {t('sessionStarted', { defaultValue: 'Session Started' })}
                </span>
                <span className="timestamp-value">
                  {activeSessionInfo?.transactionStartsAt ||
                  activeSessionInfo?.sessionStartAt ||
                  activeSessionInfo?.createdAt
                    ? ConvertUTCtoDateTime(
                        activeSessionInfo.transactionStartsAt ||
                          activeSessionInfo.sessionStartAt ||
                          activeSessionInfo.createdAt,
                      )
                    : '--'}
                </span>
              </div>
            </div>
            <div className="vertical-line-divider" />
            <div className="timestamp-item">
              <div className="timestamp-icon-wrap energy">
                <FlashIcon className="timestamp-icon" />
              </div>
              <div className="timestamp-details">
                <span className="timestamp-label">
                  {t('liveEnergy', { defaultValue: 'Live Energy' })}
                </span>
                <span className="timestamp-value energy-value">
                  {energyKwh != null ? `${ToFixedValue(energyKwh, 3)} kWh` : '0.00'}
                </span>
              </div>
            </div>
            <div className="vertical-line-divider" />
            <div className="timestamp-item">
              <div className="timestamp-icon-wrap energy">
                <FlashIcon className="timestamp-icon" />
              </div>
              <div className="timestamp-details">
                <span className="timestamp-label">
                  {t('liveCost', { defaultValue: 'Live Cost' })}
                </span>
                <span className="timestamp-value energy-value">
                  {activeSessionInfo?.totalCost != null
                    ? `${ToFixedValue(activeSessionInfo.totalCost, 2)} ${activeSessionInfo?.currency || ''}`
                    : '0.00'}{' '}
                </span>
              </div>
            </div>
            <div className="vertical-line-divider" />
            <div className="timestamp-item">
              <ClockIcon className="timestamp-icon" />
              <div className="timestamp-details">
                <span className="timestamp-label">
                  {t('duration', { defaultValue: 'Duration' })}
                </span>
                <span className="timestamp-value">{getSessionDuration(activeSessionInfo)}</span>
              </div>
            </div>
          </div>

          {/* Tariff Details info card */}
          <div className="location-charger-info-card">
            <div className="card-header">
              <div className="icon-wrapper">
                <FlashIcon className="location-icon" />
              </div>
              <span className="card-title">{t('tariffDetails')}</span>
            </div>
            <div className="card-grid">
              <div className="grid-item">
                <span className="grid-label">{t('tariffName')}</span>
                {activeSessionInfo?.tariff?.name ? (
                  <span
                    className="grid-value hyperlink"
                    role="presentation"
                    onClick={handleTariffClick}>
                    {activeSessionInfo.tariff.name}
                  </span>
                ) : (
                  <span className="grid-value">--</span>
                )}
              </div>
              <div className="vertical-line-divider" />
              <div className="grid-item">
                <span className="grid-label">{t('scope')}</span>
                <span className="grid-value">{activeSessionInfo?.tariff?.scope || '--'}</span>
              </div>
              <div className="vertical-line-divider" />
              {/* <div className="grid-item">
                <span className="grid-label">
                  {t('currentType', { defaultValue: 'Current Type' })}
                </span>
                <span className="grid-value">{activeSessionInfo?.tariff?.currentType || 'AC'}</span>
              </div>
              <div className="vertical-line-divider" /> */}
              <div className="grid-item">
                <span className="grid-label">{t('currency')}</span>
                <span className="grid-value">
                  {activeSessionInfo?.tariff?.currency || activeSessionInfo?.currency || '--'}
                </span>
              </div>
              <div className="vertical-line-divider" />
              <div className="grid-item">
                <span className="grid-label">
                  {t('connectorRatingkW')}
                </span>
                <span className="grid-value">
                  {activeSessionInfo?.tariff?.connectorRating?.currentType || ''}{' '}
                  {activeSessionInfo?.tariff?.connectorRating?.capacityKW || ''}{' '}
                  {activeSessionInfo?.tariff?.connectorRating?.unit || ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="split-column-right">
          {isLoaded && (
            <div className="mini-map">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter || { lat: 11.0168, lng: 76.9558 }}
                zoom={12}
                options={miniMapOptions}>
                <Marker
                  position={mapCenter || { lat: 11.0168, lng: 76.9558 }}
                  icon={{
                    url: getStationMarkerIcon('#3b82f6'),
                    scaledSize: new window.google.maps.Size(36, 36),
                  }}
                />
              </GoogleMap>
            </div>
          )}

          {/* Session details grid card */}
          <div className="active-session-details-grid-card">
            <div className="grid-row">
              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">{t('status', { defaultValue: 'Status' })}</span>
                </div>
                <div className="cell-value status-value">
                  <span className="status-dot green" />
                  {activeSessionInfo?.status === 'inProgress'
                    ? 'In Progress'
                    : activeSessionInfo?.status || 'In Progress'}
                </div>
              </div>

              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">
                    {t('sessionId', { defaultValue: 'Session ID' })}
                  </span>
                </div>
                <div className="cell-value session-id-value">
                  <span className="session-id">{activeSessionInfo?.ocpiId || '--'}</span>
                  {activeSessionInfo?.ocpiId && (
                    <CopyIcon
                      className="copy-btn-icon"
                      onClick={() => handleCopyToClipboard(activeSessionInfo?.ocpiId)}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="horizontal-divider-line" />

            <div className="grid-row">
              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">
                    {t('OCPPTransactionId', { defaultValue: 'OCPP Transaction ID' })}
                  </span>
                </div>
                <div className="cell-value status-value">
                  {activeSessionInfo?.transactionId || ''}
                </div>
              </div>
              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">
                    {t('locationOcpiId', { defaultValue: 'Location OCPI ID' })}{' '}
                  </span>
                </div>
                <div className="cell-value session-id-value">
                  <span className="session-id">{activeSessionInfo?.locationOcpiId || ''}</span>
                  {activeSessionInfo?.locationOcpiId && (
                    <CopyIcon
                      className="copy-btn-icon"
                      onClick={() => handleCopyToClipboard(activeSessionInfo?.locationOcpiId)}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="horizontal-divider-line" />

            <div className="grid-row">
              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">
                    {t('createdAt', { defaultValue: 'Created At' })}
                  </span>
                </div>
                <div className="cell-value status-value">
                  {ConvertUTCtoDateTime(activeSessionInfo?.createdAt) || ''}
                </div>
              </div>
              <div className="grid-cell">
                <div className="cell-header">
                  <span className="cell-label">
                    {' '}
                    {t('lastUpdated', { defaultValue: 'Last Updated' })}
                  </span>
                </div>
                <div className="cell-value status-value">
                  {activeSessionInfo?.lastUpdated || activeSessionInfo?.updatedAt
                    ? ConvertUTCtoDateTime(
                        activeSessionInfo.lastUpdated || activeSessionInfo.updatedAt,
                      )
                    : '--'}{' '}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      <SidePaneModel
        open={modelDetails.status}
        content={modelDetails.content || <div />}
        closePanel={handleClosePanel}
        title={modelDetails.title}
        topLeftContent={renderSidePanelTopLeftContent?.()}
        url={modelDetails.url || ''}
        urlNewTab={modelDetails.newTabUrl || ''}
        hideHeader={modelDetails.hideHeader}
        preventQueryClear
      />
    </div>
  );
}

NetworkActiveChargingSession.defaultProps = {
  dataList: undefined,
  sessionId: undefined,
  transactionId: undefined,
  onStopSuccess: undefined,
  autoRefreshInterval: 10000,
};

export default NetworkActiveChargingSession;
